<template>
  <div class="tab-content">
    <!-- Stats Cards -->
    <transition-group
      name="fade-in"
      mode="out-in"
      tag="div"
      class="stats-grid"
    >
      <div
        v-for="stat in stats"
        :key="stat.id"
        class="stat-card"
        :class="`stat-card-${stat.color}`"
      >
        <div class="stat-icon">
          <span class="icon is-large">
            <i :class="`fas ${stat.icon} fa-2x`"></i>
          </span>
        </div>
        <div class="stat-content">
          <div v-if="isLoadingStats" class="stat-skeleton">
            <div class="skeleton skeleton-text"></div>
            <div class="skeleton skeleton-number"></div>
          </div>
          <template v-else>
            <p class="stat-label">{{ stat.label }}</p>
            <p class="stat-value">{{ stat.value }}</p>
            <p
              v-if="stat.change"
              class="stat-change"
              :class="stat.changeClass"
            >
              <span class="icon is-small">
                <i
                  :class="`fas fa-arrow-${stat.change > 0 ? 'up' : 'down'}`"
                ></i>
              </span>
              {{ Math.abs(stat.change) }}% from last month
            </p>
          </template>
        </div>
      </div>
    </transition-group>

    <!-- Recent Activity -->
    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Recent Activity</h3>
        <div class="tabs is-small is-toggle">
          <ul>
            <li
              v-for="tab in activityTabs"
              :key="tab.key"
              :class="{ 'is-active': activeActivityTab === tab.key }"
            >
              <a @click="activeActivityTab = tab.key">
                <span class="icon is-small">
                  <i :class="`fas ${tab.icon}`"></i>
                </span>
                <span>{{ tab.label }}</span>
              </a>
            </li>
          </ul>
        </div>
      </div>

      <div class="activity-content">
        <transition name="tab-fade" mode="out-in">
          <div :key="activeActivityTab">
            <!-- Users Activity -->
            <div v-if="activeActivityTab === 'users'" class="activity-list">
              <div
                v-for="user in recentUsers"
                :key="user.id"
                class="activity-item"
                @click="$emit('open-user-modal', user)"
              >
                <div class="activity-icon">
                  <span class="icon">
                    <i class="fas fa-user"></i>
                  </span>
                </div>
                <div class="activity-details">
                  <p class="activity-title">{{ user.full_name }}</p>
                  <p class="activity-meta">{{ user.email }}</p>
                </div>
                <div class="activity-time">
                  {{ formatDate(user.created_at) }}
                </div>
              </div>
              <div v-if="recentUsers.length === 0" class="empty-activity">
                <p class="has-text-grey">No recent users</p>
              </div>
            </div>

            <!-- Companies Activity -->
            <div
              v-if="activeActivityTab === 'companies'"
              class="activity-list"
            >
              <div
                v-for="company in recentCompanies"
                :key="company.id"
                class="activity-item"
                @click="$emit('open-company-modal', company)"
              >
                <div class="activity-icon">
                  <span class="icon">
                    <i class="fas fa-building"></i>
                  </span>
                </div>
                <div class="activity-details">
                  <p class="activity-title">{{ company.name }}</p>
                  <p class="activity-meta">Company</p>
                </div>
                <div class="activity-time">
                  {{ formatDate(company.created_at) }}
                </div>
              </div>
              <div
                v-if="recentCompanies.length === 0"
                class="empty-activity"
              >
                <p class="has-text-grey">No recent companies</p>
              </div>
            </div>

            <!-- Projects Activity -->
            <div
              v-if="activeActivityTab === 'projects'"
              class="activity-list"
            >
              <div
                v-for="project in recentProjects"
                :key="project.id"
                class="activity-item"
                @click="$emit('open-project-modal', project)"
              >
                <div class="activity-icon">
                  <span class="icon">
                    <i class="fas fa-project-diagram"></i>
                  </span>
                </div>
                <div class="activity-details">
                  <p class="activity-title">{{ project.name }}</p>
                  <p class="activity-meta">
                    {{ project.company?.name || "N/A" }}
                  </p>
                </div>
                <div class="activity-time">
                  {{ formatDate(project.created_at) }}
                </div>
              </div>
              <div
                v-if="recentProjects.length === 0"
                class="empty-activity"
              >
                <p class="has-text-grey">No recent projects</p>
              </div>
            </div>

            <!-- Cameras Activity -->
            <div
              v-if="activeActivityTab === 'cameras'"
              class="activity-list"
            >
              <div
                v-for="camera in recentCameras"
                :key="camera.id"
                class="activity-item"
                @click="$emit('open-camera-modal', camera)"
              >
                <div class="activity-icon">
                  <span class="icon">
                    <i class="fas fa-video"></i>
                  </span>
                </div>
                <div class="activity-details">
                  <p class="activity-title">{{ camera.name }}</p>
                  <p class="activity-meta">
                    {{ camera.project?.name || "N/A" }}
                  </p>
                </div>
                <div class="activity-time">
                  {{ formatDate(camera.created_at) }}
                </div>
              </div>
              <div v-if="recentCameras.length === 0" class="empty-activity">
                <p class="has-text-grey">No recent cameras</p>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- Charts Section -->
    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Growth Trends</h3>
        <div class="field has-addons">
          <p class="control">
            <span class="select is-small">
              <select
                v-model="overviewPeriod"
                @change="$emit('update-overview-chart')"
              >
                <option value="1week">Last Week</option>
                <option value="1month">Last Month</option>
                <option value="3months">3 Months</option>
                <option value="6months">6 Months</option>
                <option value="1year">1 Year</option>
                <option value="custom">Custom</option>
              </select>
            </span>
          </p>
        </div>
      </div>
      <div v-if="overviewPeriod === 'custom'" class="custom-date-range">
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">From:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="overviewCustomStart"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-overview-chart')"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">To:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="overviewCustomEnd"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-overview-chart')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-wrapper">
        <LineChart
          v-if="overviewChartData"
          :key="`overview-${overviewChartKey}`"
          :chart-data="overviewChartData"
          :options="lineChartOptions"
        />
        <div v-else class="no-data-message">
          <p>N/A - Data unavailable</p>
        </div>
      </div>
    </div>

    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Camera Types Distribution</h3>
      </div>
      <div class="chart-wrapper">
        <PieChart
          v-if="cameraTypesData"
          :key="`camera-types-${cameraTypesKey}`"
          :chart-data="cameraTypesData"
          :options="pieChartOptions"
        />
        <div v-else class="no-data-message">
          <p>N/A - Data unavailable</p>
        </div>
      </div>
    </div>

    <div class="dashboard-section">
      <div class="section-header">
        <h3 class="title is-5">Camera Type Trends (Top 5)</h3>
        <div class="field has-addons">
          <p class="control">
            <span class="select is-small">
              <select
                v-model="cameraTypePeriod"
                @change="$emit('update-camera-type-trends')"
              >
                <option value="1week">Last Week</option>
                <option value="1month">Last Month</option>
                <option value="3months">3 Months</option>
                <option value="6months">6 Months</option>
                <option value="1year">1 Year</option>
                <option value="custom">Custom</option>
              </select>
            </span>
          </p>
        </div>
      </div>
      <div v-if="cameraTypePeriod === 'custom'" class="custom-date-range">
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">From:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="cameraTypeCustomStart"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-camera-type-trends')"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="field is-horizontal">
          <div class="field-label is-small">
            <label class="label">To:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  v-model="cameraTypeCustomEnd"
                  type="date"
                  class="input is-small"
                  @change="$emit('update-camera-type-trends')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="chart-wrapper">
        <LineChart
          v-if="cameraTypeTrendsData"
          :key="`camera-trends-${cameraTypeTrendsKey}`"
          :chart-data="cameraTypeTrendsData"
          :options="lineChartOptions"
        />
        <div v-else class="no-data-message">
          <p>N/A - Data unavailable</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { formatDate } from "@/utils/date_converter";
import LineChart from "../LineChart.vue";
import PieChart from "../PieChart.vue";

export default {
  name: "OverviewTab",
  components: {
    LineChart,
    PieChart,
  },
  props: {
    stats: {
      type: Array,
      required: true,
    },
    isLoadingStats: {
      type: Boolean,
      default: false,
    },
    recentUsers: {
      type: Array,
      required: true,
    },
    recentCompanies: {
      type: Array,
      required: true,
    },
    recentProjects: {
      type: Array,
      required: true,
    },
    recentCameras: {
      type: Array,
      required: true,
    },
    overviewChartData: {
      type: Object,
      default: null,
    },
    cameraTypesData: {
      type: Object,
      default: null,
    },
    cameraTypeTrendsData: {
      type: Object,
      default: null,
    },
    overviewChartKey: {
      type: Number,
      required: true,
    },
    cameraTypesKey: {
      type: Number,
      required: true,
    },
    cameraTypeTrendsKey: {
      type: Number,
      required: true,
    },
    lineChartOptions: {
      type: Object,
      required: true,
    },
    pieChartOptions: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeActivityTab: "users",
      activityTabs: [
        { key: "users", label: "Users", icon: "fa-users" },
        { key: "companies", label: "Companies", icon: "fa-building" },
        { key: "projects", label: "Projects", icon: "fa-project-diagram" },
        { key: "cameras", label: "Cameras", icon: "fa-video" },
      ],
      overviewPeriod: "1month",
      overviewCustomStart: null,
      overviewCustomEnd: null,
      cameraTypePeriod: "1month",
      cameraTypeCustomStart: null,
      cameraTypeCustomEnd: null,
    };
  },
  methods: {
    formatDate,
  },
};
</script>

<style scoped>
.chart-wrapper {
  background: white;
  padding: 1.5rem;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  height: 400px;
}

.custom-date-range {
  margin-bottom: 1rem;
  padding: 1rem;
  background: #f5f5f5;
  border-radius: 4px;
}

.custom-date-range .field-label {
  flex-basis: 80px;
  flex-grow: 0;
  margin-right: 0.75rem;
}

.custom-date-range .label {
  text-align: right;
  font-size: 0.875rem;
  margin-bottom: 0;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #7a7a7a;
  font-size: 1.1rem;
  font-weight: 500;
}

.custom-date-range .field-body {
  flex-grow: 5;
}

.tab-content {
  padding: 1.5rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

@media (max-width: 1400px) {
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 1024px) {
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s, box-shadow 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.stat-card-primary {
  border-left: 4px solid #3273dc;
}

.stat-card-info {
  border-left: 4px solid #209cee;
}

.stat-card-success {
  border-left: 4px solid #48c774;
}

.stat-card-warning {
  border-left: 4px solid #ffdd57;
}

.stat-card-danger {
  border-left: 4px solid #f14668;
}

.stat-icon {
  flex-shrink: 0;
}

.stat-card-primary .stat-icon {
  color: #3273dc;
}

.stat-card-info .stat-icon {
  color: #209cee;
}

.stat-card-success .stat-icon {
  color: #48c774;
}

.stat-card-warning .stat-icon {
  color: #ffdd57;
}

.stat-card-danger .stat-icon {
  color: #f14668;
}

.stat-content {
  flex-grow: 1;
}

.stat-label {
  font-size: 0.875rem;
  color: #7a7a7a;
  margin-bottom: 0.25rem;
}

.stat-value {
  font-size: 2rem;
  font-weight: 700;
  color: #363636;
  line-height: 1;
  margin-bottom: 0.5rem;
}

.stat-change {
  font-size: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.stat-change.is-success {
  color: #48c774;
}

.stat-change.is-danger {
  color: #f14668;
}

.stat-skeleton {
  padding: 0.5rem 0;
}

.skeleton {
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;
}

.skeleton-text {
  height: 14px;
  width: 60%;
  margin-bottom: 0.5rem;
}

.skeleton-number {
  height: 32px;
  width: 80%;
}

.dashboard-section {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-header .title {
  margin-bottom: 0;
}

.activity-content {
  margin-top: 1rem;
}

.activity-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.activity-item {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: #f9f9f9;
  border-radius: 6px;
  transition: background-color 0.2s;
  cursor: pointer;
}

.activity-item:hover {
  background: #f0f0f0;
}

.activity-icon {
  width: 40px;
  height: 40px;
  background: #3273dc;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  margin-right: 1rem;
}

.activity-icon .icon {
  color: white;
}

.activity-details {
  flex-grow: 1;
  min-width: 0;
}

.activity-title {
  font-weight: 600;
  color: #363636;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-meta {
  font-size: 0.875rem;
  color: #7a7a7a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.activity-time {
  font-size: 0.75rem;
  color: #b5b5b5;
  margin-left: 1rem;
}

.empty-activity {
  text-align: center;
  padding: 2rem;
}

.tab-fade-enter-active,
.tab-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.tab-fade-enter-from {
  opacity: 0;
  transform: translateX(-10px);
}

.tab-fade-leave-to {
  opacity: 0;
  transform: translateX(10px);
}

.tab-fade-enter-to,
.tab-fade-leave-from {
  opacity: 1;
  transform: translateX(0);
}

.fade-in-enter-active {
  transition: opacity 0.3s ease-in;
}

.fade-in-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-in-enter-to {
  opacity: 1;
  transform: scale(1);
}

@keyframes loading {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
